import { useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Pencil, Users } from 'lucide-react';

import { usePermissions } from '@/auth/usePermissions';
import { Button } from '@/components/ui/button';
import { patientQueries } from '@/features/patient-record/queries';
import type {
  CareTeamMemberDetailViewModel,
  PatientViewModel,
} from '@/models/patient';

import { Panel } from '../Panel';
import { CareTeamEditor } from './CareTeamEditor';
import { MetaRow } from './MetaRow';
import { TeamRow } from './TeamRow';
import {
  useResolveUserDisplayNames,
  type ResolvedMemberName,
  type ResolvedName,
} from './utils';

interface CareTeamPanelProps {
  readonly patient: PatientViewModel;
  readonly dragHandle?: ReactNode;
  /** Render body-only (no Panel chrome) for embedding inside another card. See {@link Panel}. */
  readonly embedded?: boolean;
  /** Controlled edit state — when provided (with {@link onEditingChange}) the host owns the edit
   *  toggle (e.g. the pedigree node rail's CollapsibleCard header). Uncontrolled internally otherwise. */
  readonly editing?: boolean;
  readonly onEditingChange?: (editing: boolean) => void;
  /** Suppress the panel's own edit button — the host provides it (see {@link editing}). */
  readonly hideHeaderAction?: boolean;
}

/**
 * Care-team display + edit dispatcher (PTV2-1095). Primary clinician lives in
 * its own slot at the top; supporting team members render below. Edit
 * affordance lives in the panel header and is gated by `canEditCareTeam`
 * per PTV2-915 (hide-don't-disable).
 */
export function CareTeamPanel({
  patient,
  dragHandle,
  embedded = false,
  editing: editingProp,
  onEditingChange,
  hideHeaderAction = false,
}: CareTeamPanelProps): ReactElement {
  const { t } = useTranslation();
  const { canEditCareTeam } = usePermissions();
  // Controlled editing when the host passes editing + onEditingChange; internal state otherwise.
  const [editingInternal, setEditingInternal] = useState(false);
  const editing = editingProp ?? editingInternal;
  const setEditing = (v: boolean): void => {
    if (onEditingChange) onEditingChange(v);
    else setEditingInternal(v);
  };
  const { program, referralSource } = patient.metadata;

  const membersQuery = useQuery(patientQueries.careTeamMembers(patient.guid));

  const members = useMemo<readonly CareTeamMemberDetailViewModel[]>(
    () => (membersQuery.isSuccess ? membersQuery.data : []),
    [membersQuery.isSuccess, membersQuery.data],
  );

  // Resolve missing display names. Optimistic rows carry their picker-supplied
  // name through the mutation cycle; server-loaded rows route through the
  // user-detail lookup until BE expands the contract. While the join is in
  // flight, callers render a skeleton — we never leak the raw user_id GUID.
  const namesByUserId = useResolveUserDisplayNames(
    members.filter((m) => m.displayName === null).map((m) => m.userId),
  );
  const resolveName = (m: CareTeamMemberDetailViewModel): ResolvedMemberName => {
    if (m.displayName !== null) return { status: 'ready', name: m.displayName };
    const resolved: ResolvedName | undefined = namesByUserId.get(m.userId);
    if (resolved === undefined || resolved.status === 'pending') {
      return { status: 'pending' };
    }
    return {
      status: 'ready',
      name: resolved.name ?? t('patient.careTeam.unknownUser'),
    };
  };

  const primary = members.find((m) => m.isPrimary) ?? null;
  const rest = members.filter((m) => !m.isPrimary);
  const hasAnyTeam = primary !== null || rest.length > 0;

  const headerActions =
    canEditCareTeam && !editing && !hideHeaderAction ? (
      <Button
        size="sm"
        variant="ghost"
        onClick={() => {
          setEditing(true);
        }}
        aria-label={t('patient.careTeam.editAriaLabel')}
        className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
      >
        <Pencil className="h-3 w-3" aria-hidden="true" />
      </Button>
    ) : null;

  return (
    <Panel
      icon={<Users className="h-3.5 w-3.5" aria-hidden="true" />}
      title={t('patient.sidePanels.careTeam')}
      headerAction={headerActions}
      collapsible
      dragHandle={dragHandle}
      embedded={embedded}
    >
      {program !== null && (
        <MetaRow
          label={t('patient.careTeam.program')}
          value={<span className="text-foreground font-medium">{program}</span>}
        />
      )}

      {membersQuery.isPending && (
        <p className="px-4 py-3 text-[12.5px] text-muted-foreground italic">
          {t('patient.careTeam.loading')}
        </p>
      )}

      {membersQuery.isError && (
        <p role="alert" className="px-4 py-3 text-[12.5px] text-destructive">
          {t('patient.careTeam.loadError')}
        </p>
      )}

      {membersQuery.isSuccess && !hasAnyTeam && (
        <p className="px-4 py-3 text-[12.5px] text-muted-foreground italic">
          {t('patient.careTeam.noTeam')}
        </p>
      )}

      {membersQuery.isSuccess && primary !== null && (
        <TeamRow
          resolved={resolveName(primary)}
          role={primary.role.length > 0 ? primary.role : t('patient.careTeam.primaryGc')}
          badge={t('patient.careTeam.primaryBadge')}
        />
      )}
      {membersQuery.isSuccess &&
        rest.map((member) => (
          <TeamRow
            key={member.id}
            resolved={resolveName(member)}
            role={member.role.length > 0 ? member.role : null}
          />
        ))}

      {referralSource !== null && (
        <MetaRow
          label={t('patient.careTeam.referralSource')}
          value={<span className="text-foreground">{referralSource}</span>}
        />
      )}

      {editing && (
        <CareTeamEditor
          guid={patient.guid}
          members={members}
          resolveName={resolveName}
          initialProgram={program}
          onClose={() => {
            setEditing(false);
          }}
        />
      )}
    </Panel>
  );
}
